/**
 * System Config Flag Exceptions Config
 *
 * Covers: ex-ls-loyalty-enabled, ex-ls-ecommerce-enabled
 *
 * FP-level configuration flags. Inspector shows a simple alert display.
 * Platform reports show FP-level table rows with a Status cell spelling
 * out which feature is enabled (backed by `status_label` from the backend).
 */
import React from 'react'
import { Chip } from '@mui/material'
import { Warning as WarningIcon } from '@mui/icons-material'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

const renderStatus = (value: any): React.ReactNode => {
  if (!value) return '-'
  return React.createElement(Chip, {
    label: value,
    color: 'warning',
    size: 'small',
    icon: React.createElement(WarningIcon),
  })
}

const columns: ExceptionColumnDef[] = [
  buildIssuesColumn(),
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'status_label',
    label: 'Status',
    contexts: ['platform'],
    render: renderStatus,
  },
  {
    key: 'exception_detected_at',
    label: 'Detected',
    type: 'datetime',
    contexts: ['platform'],
    exportFormat: 'date',
  },
]

export const configFlagConfig: ExceptionConfig = {
  codes: [
    'ex-ls-loyalty-enabled',
    'ex-ls-ecommerce-enabled',
  ],
  columns,
  defaultSortColumn: 'fp_code',
  defaultSortDirection: 'asc',
  complexity: 'simple',
  rowKeyField: 'fp_code',
  // Config flags are FP-scoped: the tag's object_id is a literal
  // ("ls_loyalty" / "ls_ecommerce") so the row has no per-item identity
  // to surface. Show a static caption so the reviewer knows there's no
  // per-row drill-down available for this exception type.
  getRowTitle: (row) => row.status_label || row.exception_type || 'Lightspeed setting enabled',
  getRowSubtitle: () => 'Lightspeed account-level setting',
}
